import type { ProductVariantsFragment } from '~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';

/**
 * Completes the given current options with the cheapest possible configuration.
 * If multiple configuration have the same (cheapest) price, the first
 * configuration from the given response is used.
 *
 * @param response
 * @param currentOptions
 * @see https://gcp.baslerweb.com/jira/browse/WEB2-2421
 */
export function fillUpMissingOptions(
  response?: ProductVariantsFragment['variants'],
  currentOptions: Record<string, string> = {}
): Record<string, string> {
  if (
    !response ||
    // assume, configuration is already completed
    Object.keys(currentOptions).length === response[0]?.attributes?.length
  ) {
    return currentOptions;
  }

  const sortedByCheapestPrice = response?.filter(isDefined).sort((a, b) => {
    const firstPrice =
      a.product?.price_range?.minimum_price?.final_price?.value;
    const secondPrice =
      b.product?.price_range?.minimum_price.final_price?.value;
    if (!firstPrice || !secondPrice) {
      return 0;
    }

    return firstPrice - secondPrice;
  });

  if (!sortedByCheapestPrice) {
    return currentOptions;
  }

  // complete configuration by cheapest variant
  const cheapestConfiguration = sortedByCheapestPrice.find((variant) =>
    Object.values(currentOptions).every((optionUid) =>
      variant.attributes?.some((attribute) => attribute?.uid === optionUid)
    )
  );

  if (cheapestConfiguration) {
    cheapestConfiguration.attributes?.forEach((attribute) => {
      if (attribute?.code) {
        currentOptions[attribute.code] = attribute.uid;
      }
    });
  }

  return currentOptions;
}
